"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { api } from "@/lib/api";
import { 
  BookOpen, 
  Calendar, 
  ArrowRight, 
  Loader2,
  ChevronRight,
  ShieldCheck,
  Search
} from "lucide-react";
import { motion } from "framer-motion";

export default function BlogsPage() {
  const [blogs, setBlogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const data = await api.getBlogs();
        setBlogs(data as any[]);
      } catch (err) {
        console.error("Failed to fetch blogs", err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const filteredBlogs = blogs.filter(b => 
    b.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
    b.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <main className="min-h-screen bg-slate-50 pt-32 pb-24 text-slate-950">
      
      {/* Background Accents */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-[320px] pointer-events-none opacity-80 z-0">
        <div className="absolute top-[-100px] right-1/4 w-[40%] h-full bg-emerald-100/60 blur-[130px] rounded-full" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        {/* Editorial Header */}
        <div className="max-w-3xl mb-16">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-4 py-2 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-700 mb-6">
            <BookOpen size={13} />
            FIXER JOURNAL
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight text-slate-900 leading-[1.05]">
            Knowledge to keep your property <span className="text-emerald-600 font-medium">running smoothly.</span>
          </h1>
          <p className="mt-6 text-lg font-medium leading-8 text-slate-500 max-w-2xl">
            Vetted maintenance insights, emergency repair guides, budgeting tips, and home care expertise straight from industry professionals.
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-12 max-w-md relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search articles & guides..."
            className="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-12 pr-4 text-sm font-semibold outline-none transition-all focus:border-emerald-600 focus:ring-4 focus:ring-emerald-50"
          />
        </div>

        {/* Blog Posts Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((n) => (
              <div key={n} className="bg-white border border-slate-200/80 rounded-3xl p-6 shadow-sm animate-pulse space-y-4">
                <div className="w-full aspect-[16/10] bg-slate-100 rounded-2xl" />
                <div className="h-4 bg-slate-100 rounded w-1/3" />
                <div className="h-6 bg-slate-100 rounded w-5/6" />
                <div className="h-10 bg-slate-100 rounded w-full" />
              </div>
            ))}
          </div>
        ) : filteredBlogs.length === 0 ? (
          <div className="text-center py-20 bg-white border border-slate-200 rounded-[2rem] shadow-sm max-w-xl mx-auto flex flex-col items-center">
            <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-6">
              <BookOpen size={28} />
            </div>
            <h3 className="text-lg font-bold text-slate-900">No articles found</h3>
            <p className="text-slate-500 text-sm mt-1 max-w-sm">We couldn't find any articles matching your search query. Try checking back later or searching for something else.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredBlogs.map((blog, idx) => (
              <motion.article
                key={blog.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
                className="group flex flex-col bg-white border border-slate-200/80 hover:border-emerald-100 hover:shadow-2xl hover:shadow-emerald-950/5 rounded-[2rem] overflow-hidden transition-all duration-300"
              >
                {/* Thumbnail */}
                <Link href={`/blogs/${blog.slug}`} className="block relative aspect-[16/10] overflow-hidden bg-slate-100 border-b border-slate-100">
                  {blog.mainImage ? (
                    <img
                      src={blog.mainImage}
                      alt={blog.title}
                      className="w-full h-full object-cover select-none transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300">
                      <BookOpen size={48} className="stroke-[1.5]" />
                    </div>
                  )}
                </Link>

                {/* Content */}
                <div className="p-7 flex flex-col flex-1">
                  
                  {/* Publication Date */}
                  <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">
                    <Calendar size={12} />
                    <span>
                      {new Date(blog.createdAt).toLocaleDateString("en-AU", {
                        day: "numeric",
                        month: "short",
                        year: "numeric"
                      })}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-slate-900 group-hover:text-emerald-700 transition-colors leading-snug">
                    <Link href={`/blogs/${blog.slug}`}>
                      {blog.title}
                    </Link>
                  </h3>
                  
                  <p className="mt-3 text-slate-500 text-sm leading-relaxed line-clamp-3">
                    {blog.description}
                  </p>

                  <div className="mt-6 pt-6 border-t border-slate-100 flex items-center justify-between mt-auto">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                      5 min read
                    </span>
                    <Link 
                      href={`/blogs/${blog.slug}`}
                      className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-emerald-700 hover:text-emerald-800 transition-colors"
                    >
                      Read Article
                      <ChevronRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </div>

                </div>

              </motion.article>
            ))}
          </div>
        )}

      </div>

    </main>
  );
}
